import { useContext } from "react";
import dayjs, { Dayjs } from "dayjs";
import { BreadBudget } from "breadcommon";
import { buildClasses } from "../utils/buildClasses";
import { RyeButton } from "../rye/RyeButton";
import { RyeIcon } from "../rye/RyeIcon";
import { RyeInput } from "../rye/RyeInput";
import { RyeMonthPicker } from "../rye/RyeMonthPicker";
import { RyeDropDown } from "../rye/RyeDropDown";
import { getCurrencySymbol } from "../common/DollarAmount";
import { SUPPORTED_CURRENCIES } from "../firebaseio/ExchangeRatesContext";
import { UserContext } from "../firebaseio/UserContext";
import { firestoreUpdateBudget } from "../firebaseio/firestoreIo";
import { budgetMonths, budgetProgress } from "./budgetUtils";

/**
 * The selected budget at the top of both views: its name, which also picks
 * the budget, the months it covers beside it, and the actions on it. While
 * editing, the name, months, and currency become editable; save and discard
 * are then in the top bar, so they stay put as the body scrolls.
 */
export function BudgetHeader({
  budget,
  budgets,
  onSelectBudget,
  isEditing,
  focusNameInput,
  onEdit,
  onDuplicate,
  onDelete,
}: {
  budget: BreadBudget;
  /** Every budget, in the order the picker lists them. */
  budgets: BreadBudget[];
  onSelectBudget: (budgetId: string) => void;
  isEditing: boolean;
  focusNameInput: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}): JSX.Element {
  const user = useContext(UserContext);

  async function updateName(name: string) {
    await firestoreUpdateBudget(user.uid, budget.id, { name });
  }

  async function updateStartMonth(month: Dayjs | null) {
    if (!month) return;
    const start = month.startOf("month");
    // Moving the start past the end moves the end along with it.
    await firestoreUpdateBudget(
      user.uid,
      budget.id,
      start.isAfter(budget.endDateTimestampSecs)
        ? {
            begin_date_timestamp_secs: start.unix(),
            end_date_timestamp_secs: month.endOf("month").unix(),
          }
        : { begin_date_timestamp_secs: start.unix() },
    );
  }

  async function updateEndMonth(month: Dayjs | null) {
    if (!month) return;
    await firestoreUpdateBudget(user.uid, budget.id, {
      end_date_timestamp_secs: month.endOf("month").unix(),
    });
  }

  const detailRow = buildClasses(
    "flex",
    "items-center",
    "gap-2",
    "text-sm",
    "text-on-surface-600",
  );

  return (
    <div
      className={buildClasses(
        "flex",
        "items-center",
        "gap-3",
        "min-h-11",
        "mb-6",
      )}
    >
      {isEditing ? (
        <>
          <RyeInput
            startingText={budget.name}
            size="lg"
            fontWeight="heavy"
            width="lg"
            focusOnInit={focusNameInput}
            onChange={updateName}
          />
          <div className={buildClasses(detailRow, "shrink-0")}>
            <RyeMonthPicker
              selectedDate={budget.beginDateTimestampSecs}
              onDateChange={updateStartMonth}
              size="sm"
              width="xs"
            />
            through
            <RyeMonthPicker
              selectedDate={budget.endDateTimestampSecs}
              onDateChange={updateEndMonth}
              size="sm"
              width="xs"
            />
            {/* Set apart a little from the months it isn't part of. */}
            <div className={buildClasses("ml-2")}>
              <RyeDropDown<string>
                entries={SUPPORTED_CURRENCIES.map((currency) => ({
                  id: currency,
                  text: currency,
                  icon: (
                    <span
                      className={buildClasses(
                        "font-mono",
                        "text-sm",
                        "font-semibold",
                        "w-5",
                        "text-center",
                        "text-on-surface-600",
                        "select-none",
                      )}
                    >
                      {getCurrencySymbol("en-US", currency)}
                    </span>
                  ),
                }))}
                initEntryId={budget.currency}
                onSelectEntry={async (entry) =>
                  await firestoreUpdateBudget(user.uid, budget.id, {
                    currency: entry.id,
                  })
                }
                searchable={true}
                size="sm"
                width="xs"
              />
            </div>
          </div>
        </>
      ) : (
        <>
          <BudgetPicker
            budget={budget}
            budgets={budgets}
            onSelectBudget={onSelectBudget}
          />
          <div className={buildClasses(detailRow, "gap-1.5", "shrink-0")}>
            <RyeIcon name="calendar_month" size="sm" />
            <span>{budgetMonths(budget)}</span>
            <span className={buildClasses("text-on-surface-500")}>
              · {budgetProgress(budget, dayjs())}
            </span>
          </div>
        </>
      )}
      <div className={buildClasses("flex", "items-center", "gap-2", "ml-auto")}>
        {!isEditing && (
          <>
            <RyeButton
              text="Edit"
              icon="edit"
              size="sm"
              vibe="subdued"
              onClick={onEdit}
            />
            <RyeButton
              tooltip="Duplicate budget"
              icon="content_copy"
              size="sm"
              vibe="subdued"
              onClick={onDuplicate}
            />
            <RyeButton
              tooltip="Delete budget"
              icon="delete"
              size="sm"
              vibe="subdued"
              onClick={onDelete}
            />
          </>
        )}
      </div>
    </div>
  );
}

/** The budget's name as a heading, which opens a list of budgets to switch to. */
function BudgetPicker({
  budget,
  budgets,
  onSelectBudget,
}: {
  budget: BreadBudget;
  budgets: BreadBudget[];
  onSelectBudget: (budgetId: string) => void;
}): JSX.Element {
  return (
    <div className={buildClasses("min-w-0")}>
      <RyeDropDown<string>
        entries={budgets.map((option) => ({
          id: option.id,
          text: option.name,
          icon: null,
          subtitle: budgetMonths(option),
        }))}
        initEntryId={budget.id}
        onSelectEntry={(entry) => {
          if (entry.id !== budget.id) onSelectBudget(entry.id);
        }}
        size="xl"
        width="auto"
        fontWeight="medium"
      />
    </div>
  );
}
